// src/contexts/NotificationContext.tsx
import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback, useRef } from 'react';
import { io, Socket } from 'socket.io-client';

export interface Notification {
  id: string;
  type: 'info' | 'warning' | 'error' | 'success';
  title: string;
  message: string;
  timestamp: Date;
  read: boolean;
  category: 'system' | 'device' | 'maintenance' | 'safety' | 'fuel' | 'geofence' | 'vehicle_request';
  deviceId?: string;
  priority: 'low' | 'medium' | 'high' | 'critical';
  metadata?: any;
}

export interface ToastNotification {
  id: string;
  type: 'info' | 'warning' | 'error' | 'success';
  title: string;
  message: string;
  priority: 'low' | 'medium' | 'high' | 'critical';
  category: string;
  metadata?: any;
}

interface NotificationContextType {
  notifications: Notification[];
  unreadCount: number;
  hasMore: boolean;
  loading: boolean;
  toast: ToastNotification | null;
  dismissToast: () => void;
  loadMore: () => Promise<void>;
  addNotification: (notification: Omit<Notification, 'id' | 'timestamp' | 'read'>) => void;
  markAsRead: (id: string) => void;
  markAllAsRead: () => void;
  removeNotification: (id: string) => void;
  clearAllNotifications: () => void;
  socket: Socket | null;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

// ─── Audio unlock ─────────────────────────────────────────────────────────────
// Browsers block autoplay until the user has interacted with the page.
// We pre-load and silently unlock the audio on the first click/keydown.
let audioUnlocked = false;
let audioElement: HTMLAudioElement | null = null;

function getAudio(): HTMLAudioElement {
  if (!audioElement) {
    audioElement = new Audio('/mixkit-message-pop-alert-2354.mp3');
    audioElement.volume = 0.5;
    audioElement.preload = 'auto';
  }
  return audioElement;
}

function unlockAudio() {
  if (audioUnlocked) return;
  const audio = getAudio();
  // Play silently to unlock — immediately pause so nothing is heard
  audio.volume = 0;
  audio.play()
    .then(() => {
      audio.pause();
      audio.currentTime = 0;
      audio.volume = 0.5;
      audioUnlocked = true;
      console.log('🔊 Audio unlocked');
    })
    .catch(() => { });
}

function playNotificationSound() {
  try {
    const audio = getAudio();
    audio.currentTime = 0;
    audio.volume = 0.5;
    audio.play().catch((err) => console.log('🔇 Sound blocked:', err.message));
  } catch {
    console.log('🔇 Audio not supported');
  }
}

export const NotificationProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [socket, setSocket] = useState<Socket | null>(null);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState<ToastNotification | null>(null);

  const refetchTimerRef = useRef<NodeJS.Timeout | null>(null);
  const toastTimerRef = useRef<NodeJS.Timeout | null>(null);

  const unreadCount = notifications.filter((n) => !n.read).length;

  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost/api';
  const WS_URL = process.env.REACT_APP_WS_URL || 'http://localhost';

  // ─── Audio unlock on first interaction ──────────────────────────────────
  useEffect(() => {
    const unlock = () => unlockAudio();
    window.addEventListener('click', unlock, { once: true });
    window.addEventListener('keydown', unlock, { once: true });
    window.addEventListener('touchstart', unlock, { once: true });
    return () => {
      window.removeEventListener('click', unlock);
      window.removeEventListener('keydown', unlock);
      window.removeEventListener('touchstart', unlock);
    };
  }, []);

  // ─── Show toast ──────────────────────────────────────────────────────────
  const showToast = useCallback((data: ToastNotification) => {
    setToast(data);
    // Auto-dismiss after 6s for normal, 10s for critical
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    const duration = data.priority === 'critical' ? 10000 : 6000;
    toastTimerRef.current = setTimeout(() => setToast(null), duration);
  }, []);

  const dismissToast = useCallback(() => {
    setToast(null);
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
  }, []);

  // ─── Fetch notifications ─────────────────────────────────────────────────
  const fetchNotifications = useCallback(async (skip = 0, append = false) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      setLoading(true);
      const LIMIT = 20;

      const response = await fetch(
        `${API_URL}/notifications?limit=${LIMIT}&skip=${skip}&unreadOnly=false&includeArchived=false`,
        { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } }
      );

      if (response.ok) {
        const data = await response.json();
        if (data.success && data.data) {
          const fetched: Notification[] = data.data.map((n: any) => ({
            id: n._id,
            type: n.type,
            category: n.category,
            title: n.title,
            message: n.message,
            timestamp: new Date(n.createdAt || n.timestamp),
            read: n.read,
            priority: n.priority,
            deviceId: n.deviceId,
            metadata: n.metadata || {},
          }));

          setNotifications((prev) => append ? [...prev, ...fetched] : fetched);
          setHasMore(fetched.length === LIMIT);
        }
      }
    } catch (err) {
      console.error('❌ Fetch notifications error:', err);
    } finally {
      setLoading(false);
    }
  }, [API_URL]);

  // Initial fetch
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) fetchNotifications(0, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Debounced refetch
  const debouncedRefetch = useCallback(() => {
    if (refetchTimerRef.current) clearTimeout(refetchTimerRef.current);
    refetchTimerRef.current = setTimeout(() => {
      fetchNotifications(0, false);
      refetchTimerRef.current = null;
    }, 500);
  }, [fetchNotifications]);

  // ─── Socket.IO ───────────────────────────────────────────────────────────
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;

    console.log('🔌 Connecting WebSocket:', WS_URL);

    const newSocket = io(WS_URL, {
      transports: ['websocket', 'polling'],
      auth: { token },
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionAttempts: 5,
    });

    newSocket.on('connect', () => {
      console.log('✅ WebSocket connected');

      const userStr = localStorage.getItem('user');
      if (userStr) {
        try {
          const user = JSON.parse(userStr);
          const companyId = user.companyId?._id || user.companyId;
          if (companyId) {
            console.log(`🏢 Joining company_${companyId}`);
            newSocket.emit('join_company_room', companyId);
          }
        } catch { }
      }
    });

    newSocket.on('room_joined', (data: any) => {
      console.log('✅ Joined room:', data.room);
    });

    newSocket.on('connect_error', (err) => {
      console.error('❌ Socket error:', err.message);
    });

    // ── Catch-all for debugging (remove in production) ──
    newSocket.onAny((eventName: string, data: any) => {
      console.log(`🔔 Socket event: "${eventName}"`, data);
    });

    // ── Geofence notification ─────────────────────────────────────────────
    newSocket.on('geofence_notification', (data: any) => {
      console.log('🛡️ Geofence notification:', data);

      // Show toast popup immediately
      showToast({
        id: data.id || `toast_${Date.now()}`,
        type: data.type || 'info',
        title: data.title || 'Geofence Alert',
        message: data.message || '',
        priority: data.priority || 'medium',
        category: 'geofence',
        metadata: data.metadata || {},
      });

      // Play sound
      playNotificationSound();

      // Refetch from DB to update the bell
      debouncedRefetch();
    });

    // ── Generic notification ──────────────────────────────────────────────
    newSocket.on('notification', (data: any) => {
      console.log('📬 Notification:', data);

      showToast({
        id: data.id || `toast_${Date.now()}`,
        type: data.type || 'info',
        title: data.title || 'Notification',
        message: data.message || '',
        priority: data.priority || 'medium',
        category: data.category || 'system',
        metadata: data.metadata || {},
      });

      if (data.priority === 'critical' || data.priority === 'high') {
        playNotificationSound();
      }

      debouncedRefetch();
    });

    // ── Device alert ──────────────────────────────────────────────────────
    newSocket.on('device_alert', (data: any) => {
      console.log('📡 Device alert:', data);

      showToast({
        id: `toast_${Date.now()}`,
        type: 'warning',
        title: data.title || 'Device Alert',
        message: data.message || '',
        priority: 'high',
        category: 'device',
        metadata: data,
      });

      playNotificationSound();
      debouncedRefetch();
    });

    // ── Security alert ────────────────────────────────────────────────────
    newSocket.on('security_alert', (data: any) => {
      console.log('🚨 Security alert:', data);

      showToast({
        id: `toast_${Date.now()}`,
        type: 'error',
        title: '🚨 Security Alert',
        message: data.message || 'Unauthorized activity detected',
        priority: 'critical',
        category: 'safety',
        metadata: data,
      });

      playNotificationSound();
      debouncedRefetch();
    });

    setSocket(newSocket);

    // Request browser notification permission
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }

    return () => {
      if (refetchTimerRef.current) clearTimeout(refetchTimerRef.current);
      if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
      newSocket.close();
    };
  }, [WS_URL, debouncedRefetch, showToast]);

  // ─── Load more ───────────────────────────────────────────────────────────
  const loadMore = useCallback(async () => {
    if (!hasMore || loading) return;
    await fetchNotifications(notifications.length, true);
  }, [notifications.length, hasMore, loading, fetchNotifications]);

  // ─── Add (local/manual) ──────────────────────────────────────────────────
  const addNotification = (notification: Omit<Notification, 'id' | 'timestamp' | 'read'>) => {
    const n: Notification = {
      ...notification,
      id: `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      timestamp: new Date(),
      read: false,
    };
    setNotifications((prev) => [n, ...prev]);
    if (notification.priority === 'critical' || notification.priority === 'high') {
      playNotificationSound();
    }
  };

  // ─── Mark as read ────────────────────────────────────────────────────────
  const markAsRead = async (id: string) => {
    setNotifications((prev) =>
      prev.map((n) => (n.id === id ? { ...n, read: true } : n))
    );
    try {
      const token = localStorage.getItem('token');
      await fetch(`${API_URL}/notifications/${id}/read`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      });
    } catch { }
  };

  // ─── Mark all as read ────────────────────────────────────────────────────
  const markAllAsRead = async () => {
    setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
    try {
      const token = localStorage.getItem('token');
      await fetch(`${API_URL}/notifications/mark-all-read`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      });
    } catch { }
  };

  // ─── Remove ──────────────────────────────────────────────────────────────
  const removeNotification = async (id: string) => {
    setNotifications((prev) => prev.filter((n) => n.id !== id));
    try {
      const token = localStorage.getItem('token');
      await fetch(`${API_URL}/notifications/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      });
    } catch { }
  };

  // ─── Clear all ───────────────────────────────────────────────────────────
  const clearAllNotifications = async () => {
    const ids = notifications.map((n) => n.id);
    setNotifications([]);
    try {
      const token = localStorage.getItem('token');
      await fetch(`${API_URL}/notifications/bulk-action`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ notificationIds: ids, action: 'archive' }),
      });
    } catch { }
  };

  return (
    <NotificationContext.Provider
      value={{
        notifications,
        unreadCount,
        hasMore,
        loading,
        toast,
        dismissToast,
        loadMore,
        addNotification,
        markAsRead,
        markAllAsRead,
        removeNotification,
        clearAllNotifications,
        socket,
      }}
    >
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotifications = (): NotificationContextType => {
  const ctx = useContext(NotificationContext);
  if (!ctx) throw new Error('useNotifications must be used within NotificationProvider');
  return ctx;
};